import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api";
import type { ProjectCollaboration, StickyNote, UserPermission } from "@/types/collaboration";
import { useToast } from "@/hooks/use-toast";

export const useCollaboration = (projectId: string, currentUserId: string) => {
  const [collaboration, setCollaboration] = useState<ProjectCollaboration | null>(null);
  const [permission, setPermission] = useState<UserPermission | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (projectId && currentUserId) {
      loadCollaboration();
      loadPermission();
    }
  }, [projectId, currentUserId]);

  const loadCollaboration = async () => {
    try {
      const data = await apiClient.getProjectCollaboration(projectId) as ProjectCollaboration;
      setCollaboration(data);
    } catch (error: any) {
      console.error("Failed to load collaboration:", error);
      // Don't show error toast for permission denied - user might not have access
      if (!error.message?.includes("403") && !error.message?.includes("don't have access")) {
        toast({
          title: "Error",
          description: "Failed to load collaboration data",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadPermission = async () => {
    try {
      const data = await apiClient.checkUserPermission(projectId) as UserPermission;
      setPermission(data);
    } catch (error: any) {
      console.error("Failed to load permission:", error);
    }
  };

  const addStickyNote = async (recommendationId: string, content: string) => {
    try {
      await apiClient.addStickyNote(projectId, recommendationId, content);
      await loadCollaboration(); // Reload to get updated notes
      toast({
        title: "Success",
        description: "Comment added successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add comment",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateStickyNote = async (noteId: string, content: string) => {
    try {
      await apiClient.updateStickyNote(projectId, noteId, content);
      await loadCollaboration(); // Reload to get updated notes
      toast({
        title: "Success",
        description: "Comment updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update comment",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteStickyNote = async (noteId: string) => {
    try {
      await apiClient.deleteStickyNote(projectId, noteId);
      await loadCollaboration(); // Reload to get updated notes
      toast({
        title: "Success",
        description: "Comment deleted successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete comment",
        variant: "destructive",
      });
      throw error;
    }
  };

  const isOwner = collaboration?.owner_id === currentUserId;
  // Owners have all permissions, otherwise use permission from API
  const canView = isOwner ? true : (permission?.can_view ?? false);
  const canComment = isOwner ? true : (permission?.can_comment ?? false);
  const canEdit = isOwner ? true : (permission?.can_edit ?? false);
  
  // Debug logging
  console.log('[use-collaboration] Debug:', {
    projectId,
    currentUserId,
    isOwner,
    permission,
    canView,
    canComment,
    canEdit,
    collaboration: collaboration ? {
      owner_id: collaboration.owner_id,
      collaborators_count: collaboration.collaborators?.length
    } : null
  });

  return {
    collaboration,
    permission,
    isLoading,
    isOwner,
    canView,
    canComment,
    canEdit,
    stickyNotes: collaboration?.sticky_notes ?? [],
    collaborators: collaboration?.collaborators ?? [],
    recentActivity: collaboration?.recent_activity ?? [],
    addStickyNote,
    updateStickyNote,
    deleteStickyNote,
    refresh: loadCollaboration,
  };
};